export enum FeatureFlags {
    COMPANY_DISABLED = 'COMPANY_DISABLED',
    COMPANY_READONLY = 'COMPANY_READONLY',
    SCREENING_INTERVIEW_VARIANT = 'SCREENING_INTERVIEW_VARIANT',
    ENTERPRISE_DEPARTMENT_MAPPING = 'ENTERPRISE_DEPARTMENT_MAPPING',
    JOB_POSTING_CONFIG = 'JOB_POSTING_CONFIG',
    SELF_SCHEDULING_ENABLED = 'SELF_SCHEDULING_ENABLED',
    INTERVIEW_LOCATION_ENABLED = 'INTERVIEW_LOCATION_ENABLED',
    PANELIST_FEEDBACK_AI_SUMMARY = 'PANELIST_FEEDBACK_AI_SUMMARY',
    PANELIST_FEEDBACK_NEW_UI = 'PANELIST_FEEDBACK_NEW_UI',
    PANELIST_PARAMETER_ASSIGNMENT = 'PANELIST_PARAMETER_ASSIGNMENT',
    CHEAT_DETECTION_ENABLED = 'CHEAT_DETECTION_ENABLED',
    INTERVIEW_QUALITY_ANALYSIS_ENABLED = 'INTERVIEW_QUALITY_ANALYSIS_ENABLED',
    CREATE_AI_INTERVIEWS = 'CREATE_AI_INTERVIEWS',
    AI_PHONE_SCREENING_ENABLED = 'AI_PHONE_SCREENING_ENABLED',
    WORKSHORTS = 'WORKSHORTS',
    INTERVIEW_ROUNDS_CARDS = 'INTERVIEW_ROUNDS_CARDS',
    JOB_OPENING_ADMIN_ONLY_EDIT = 'JOB_OPENING_ADMIN_ONLY_EDIT',
    ADVANCED_FOLLOWUP_OPTIONS = 'ADVANCED_FOLLOWUP_OPTIONS',
    SCREENING_SCREEN_SHARE_ENABLED = 'SCREENING_SCREEN_SHARE_ENABLED',
    SCREENING_FULLSCREEN_ENFORCEMENT_ENABLED = 'SCREENING_FULLSCREEN_ENFORCEMENT_ENABLED',
    SCREENING_LAPTOP_ONLY_ENABLED = 'SCREENING_LAPTOP_ONLY_ENABLED',
    SCREENING_LIVENESS_CHECK_ENABLED = 'SCREENING_LIVENESS_CHECK_ENABLED',
    SCREENING_NO_FACE_COUNTDOWN_LEAVE_ENABLED = 'SCREENING_NO_FACE_COUNTDOWN_LEAVE_ENABLED',
    SCREENING_AUDIO_ENHANCEMENTS_ENABLED = 'SCREENING_AUDIO_ENHANCEMENTS_ENABLED',
    CANDIDATE_STATUSES_CONFIG = 'CANDIDATE_STATUSES_CONFIG',
    COMMUNICATION_TEMPLATES = 'COMMUNICATION_TEMPLATES',
    MARLEE_ENABLED = 'MARLEE_ENABLED',
    WHATSAPP_REMINDERS_ENABLED = 'WHATSAPP_REMINDERS_ENABLED',
    PHONE_SCREENING_WHATSAPP_ENABLED = 'PHONE_SCREENING_WHATSAPP_ENABLED',
    EMPLOYEE_POOL = 'EMPLOYEE_POOL',
    AI_ANALYTICS_ENABLED = 'AI_ANALYTICS_ENABLED',
    USE_MARKITDOWN = 'USE_MARKITDOWN',
    ADMIN_CAN_BYPASS_APPROVAL = 'ADMIN_CAN_BYPASS_APPROVAL',
    DARK_MODE_ENABLED = 'DARK_MODE_ENABLED',
    CONTRACT_BILLING = 'CONTRACT_BILLING',
    RESUME_PROCESSING_CONCURRENCY = 'RESUME_PROCESSING_CONCURRENCY',
    INTERVIEW_RECORDING_VIDEO_KBPS = 'INTERVIEW_RECORDING_VIDEO_KBPS',
    JOB_ACTIVITY_TAB = 'JOB_ACTIVITY_TAB',
    JOB_APPLICATIONS_BOARD = 'JOB_APPLICATIONS_BOARD',
    CAREER_TALENT_POOL_APPLY = 'CAREER_TALENT_POOL_APPLY',
    AGENT_MEMORY_ENABLED = 'AGENT_MEMORY_ENABLED',
    AGENT_MEMORY_PORTABLE_ENABLED = 'AGENT_MEMORY_PORTABLE_ENABLED',
    AGENT_MEMORY_HASHED_IDENTITY_ENABLED = 'AGENT_MEMORY_HASHED_IDENTITY_ENABLED',
    SOURCING_EXTENSION = 'SOURCING_EXTENSION',
}

export type ScreeningInterviewVariant = 'DAILY_JS' | 'FASTRTC'

export const SCREENING_INTERVIEW_VARIANT_VALUES: ScreeningInterviewVariant[] = ['DAILY_JS', 'FASTRTC']

export const SCREENING_INTERVIEW_VARIANT_OPTIONS: { value: ScreeningInterviewVariant; label: string }[] = [
    { value: 'DAILY_JS', label: 'Daily.js' },
    { value: 'FASTRTC', label: 'FastRTC' },
]

export const FEATURE_FLAG_CATEGORIES = [
    'Access',
    'Agent',
    'Analytics',
    'Organization',
    'Job Postings',
    'Job Applications',
    'Human Interview',
    'AI Screening',
    'User Interface',
] as const

export type FeatureFlagCategory = (typeof FEATURE_FLAG_CATEGORIES)[number]

type FeatureFlagMetaBase = {
    id: FeatureFlags
    title: string
    description: string
    category: FeatureFlagCategory[]
}

type BooleanFeatureFlagMeta = FeatureFlagMetaBase & {
    kind: 'boolean'
}

type EnumFeatureFlagMeta = FeatureFlagMetaBase & {
    kind: 'enum'
    options: typeof SCREENING_INTERVIEW_VARIANT_OPTIONS
}

type NumberFeatureFlagMeta = FeatureFlagMetaBase & {
    kind: 'number'
}

export type CompanyFeatureFlagMeta = BooleanFeatureFlagMeta | EnumFeatureFlagMeta | NumberFeatureFlagMeta

export const COMPANY_FEATURE_FLAGS: Record<FeatureFlags, CompanyFeatureFlagMeta> = {
    [FeatureFlags.COMPANY_DISABLED]: {
        id: FeatureFlags.COMPANY_DISABLED,
        title: 'Company Disabled',
        description: 'Disable access to the company for all users',
        category: ['Access'],
        kind: 'boolean' as const,
    },
    [FeatureFlags.COMPANY_READONLY]: {
        id: FeatureFlags.COMPANY_READONLY,
        title: 'Company Read-only',
        description: 'Disable create, edit, delete and related mutations in the UI for this company',
        category: ['Access'],
        kind: 'boolean',
    },
    [FeatureFlags.SCREENING_INTERVIEW_VARIANT]: {
        id: FeatureFlags.SCREENING_INTERVIEW_VARIANT,
        title: 'AI Interview Variant',
        description: 'Default transport for new AI interviews (Daily.js or FastRTC)',
        category: ['AI Screening'],
        kind: 'enum',
        options: SCREENING_INTERVIEW_VARIANT_OPTIONS,
    },
    [FeatureFlags.JOB_POSTING_CONFIG]: {
        id: FeatureFlags.JOB_POSTING_CONFIG,
        title: 'Job posting configuration',
        description: 'Show the Job Posting settings tab and allow editing job posting field configuration.',
        category: ['Job Postings'],
        kind: 'boolean',
    },
    [FeatureFlags.ENTERPRISE_DEPARTMENT_MAPPING]: {
        id: FeatureFlags.ENTERPRISE_DEPARTMENT_MAPPING,
        title: 'Per-user department mapping (enterprise)',
        description: 'When enabled, users can be assigned to specific departments. When disabled, every user belongs to every department.',
        category: ['Organization'],
        kind: 'boolean',
    },
    [FeatureFlags.SELF_SCHEDULING_ENABLED]: {
        id: FeatureFlags.SELF_SCHEDULING_ENABLED,
        title: 'Interview Self Scheduling',
        description: 'Enable cooperative panelist-led automatic slot selection and self-scheduling for interviews.',
        category: ['Human Interview'],
        kind: 'boolean',
    },
    [FeatureFlags.INTERVIEW_LOCATION_ENABLED]: {
        id: FeatureFlags.INTERVIEW_LOCATION_ENABLED,
        title: 'Face-to-Face Interview Location',
        description:
            'Capture an optional physical interview location when scheduling face-to-face interviews (shown on calendar invites and confirmation emails when provided).',
        category: ['Human Interview'],
        kind: 'boolean',
    },
    [FeatureFlags.PANELIST_FEEDBACK_AI_SUMMARY]: {
        id: FeatureFlags.PANELIST_FEEDBACK_AI_SUMMARY,
        title: 'Panelist Feedback AI Summary',
        description: 'Enable AI-generated interview summary, transcript fetching, and suggested questions on the panelist feedback page.',
        category: ['Human Interview'],
        kind: 'boolean',
    },
    [FeatureFlags.PANELIST_FEEDBACK_NEW_UI]: {
        id: FeatureFlags.PANELIST_FEEDBACK_NEW_UI,
        title: 'Panelist Feedback New UI',
        description: 'Enable the redesigned panelist feedback form layout (sidebar navigation and updated sections) instead of the legacy layout.',
        category: ['Human Interview', 'User Interface'],
        kind: 'boolean',
    },
    [FeatureFlags.PANELIST_PARAMETER_ASSIGNMENT]: {
        id: FeatureFlags.PANELIST_PARAMETER_ASSIGNMENT,
        title: 'Panelist Parameter Assignment',
        description:
            'When scheduling multi-panelist human interviews, require assigning which assessment parameters each panelist should score before confirmation.',
        category: ['Human Interview'],
        kind: 'boolean',
    },
    [FeatureFlags.CHEAT_DETECTION_ENABLED]: {
        id: FeatureFlags.CHEAT_DETECTION_ENABLED,
        title: 'Cheat Detection',
        description:
            'Enable AI-powered cheat detection for screening interviews (analyzes transcripts for AI-generated responses, timing anomalies, and inconsistencies).',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.INTERVIEW_QUALITY_ANALYSIS_ENABLED]: {
        id: FeatureFlags.INTERVIEW_QUALITY_ANALYSIS_ENABLED,
        title: 'Interview Quality Analysis',
        description:
            'After an AI interview (web or phone) is scored, analyze how the AI interviewer behaved (missed turns, repeated or skipped questions, latency, judge review). Only sessions with issues are saved; results are visible to super admins only.',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.CREATE_AI_INTERVIEWS]: {
        id: FeatureFlags.CREATE_AI_INTERVIEWS,
        title: 'Create AI Interviews',
        description: 'Allow creating new AI interviews from the candidate profile.',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.AI_PHONE_SCREENING_ENABLED]: {
        id: FeatureFlags.AI_PHONE_SCREENING_ENABLED,
        title: 'AI Phone Screening',
        description: 'Enable AI phone call screening and scheduling for candidates.',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.SOURCING_EXTENSION]: {
        id: FeatureFlags.SOURCING_EXTENSION,
        title: 'Sourcing Chrome Extension',
        description: 'Let recruiters add candidates from a profile page (LinkedIn, Naukri, etc.) with the intalent.ai Chrome extension.',
        category: ['Job Applications'],
        kind: 'boolean',
    },
    [FeatureFlags.WORKSHORTS]: {
        id: FeatureFlags.WORKSHORTS,
        title: 'WorkShorts',
        description: 'Allow configuring and using WorkShorts in job postings and applications.',
        category: ['Job Postings'],
        kind: 'boolean',
    },
    [FeatureFlags.INTERVIEW_ROUNDS_CARDS]: {
        id: FeatureFlags.INTERVIEW_ROUNDS_CARDS,
        // The key still says "cards" (it's stored in company and plan flag JSON); the view it turns on is the scorecard.
        title: 'Interview Rounds Scorecard View',
        description: 'Show interview rounds as a scorecard (competencies × rounds) instead of the table view',
        category: ['Human Interview'],
        kind: 'boolean',
    },
    [FeatureFlags.JOB_OPENING_ADMIN_ONLY_EDIT]: {
        id: FeatureFlags.JOB_OPENING_ADMIN_ONLY_EDIT,
        title: 'Position status & date — Admin only',
        description: 'When enabled, only Admin users can edit position status and requisition date on the Positions tab.',
        category: ['Job Applications'],
        kind: 'boolean',
    },
    [FeatureFlags.ADVANCED_FOLLOWUP_OPTIONS]: {
        id: FeatureFlags.ADVANCED_FOLLOWUP_OPTIONS,
        title: 'Advanced Follow-up Options',
        description:
            'When enabled, shows Generic/Intensive follow-up options and allows changing scoring method. When disabled, defaults to STAR method and normal follow-up.',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.SCREENING_SCREEN_SHARE_ENABLED]: {
        id: FeatureFlags.SCREENING_SCREEN_SHARE_ENABLED,
        title: 'Screening Screen Share',
        description: 'When enabled, candidates must share their screen during FastRTC AI interviews. Required in pre-check before the interview starts.',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.SCREENING_FULLSCREEN_ENFORCEMENT_ENABLED]: {
        id: FeatureFlags.SCREENING_FULLSCREEN_ENFORCEMENT_ENABLED,
        title: 'Screening Fullscreen Enforcement',
        description: 'When enabled, candidates must remain in fullscreen mode during FastRTC AI interviews.',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.SCREENING_LAPTOP_ONLY_ENABLED]: {
        id: FeatureFlags.SCREENING_LAPTOP_ONLY_ENABLED,
        title: 'Screening Laptop Only',
        description: 'When enabled, candidates on phones and tablets cannot proceed to FastRTC interview device setup.',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.SCREENING_LIVENESS_CHECK_ENABLED]: {
        id: FeatureFlags.SCREENING_LIVENESS_CHECK_ENABLED,
        title: 'Screening Liveness Check',
        description:
            'When enabled, candidates may be required to complete face liveness verification before AI interviews (also subject to per-job screening config).',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.SCREENING_NO_FACE_COUNTDOWN_LEAVE_ENABLED]: {
        id: FeatureFlags.SCREENING_NO_FACE_COUNTDOWN_LEAVE_ENABLED,
        title: 'Screening No-Face Countdown Leave',
        description:
            'When enabled, FastRTC AI interviews show a 30-second countdown if the candidate face is not visible, then automatically end the interview.',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.SCREENING_AUDIO_ENHANCEMENTS_ENABLED]: {
        id: FeatureFlags.SCREENING_AUDIO_ENHANCEMENTS_ENABLED,
        title: 'Screening Audio Enhancements',
        description:
            'Enable enhanced interview audio for FastRTC AI interviews: mic ducking during bot playback with barge-in release, speaker mute on interruption, far-field makeup gain, and ambient noise metering in pre-check.',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.CANDIDATE_STATUSES_CONFIG]: {
        id: FeatureFlags.CANDIDATE_STATUSES_CONFIG,
        title: 'Candidate Statuses Configuration',
        description: 'Show the Candidate Statuses settings tab and allow editing custom candidate status definitions.',
        category: ['Job Applications'],
        kind: 'boolean',
    },
    [FeatureFlags.COMMUNICATION_TEMPLATES]: {
        id: FeatureFlags.COMMUNICATION_TEMPLATES,
        title: 'Communication Templates',
        description: 'Enable custom communication templates for emails.',
        category: ['Organization'],
        kind: 'boolean',
    },
    [FeatureFlags.MARLEE_ENABLED]: {
        id: FeatureFlags.MARLEE_ENABLED,
        title: 'Marlee Chatbot',
        description: 'Enable the Marlee AI chatbot panel for screening, shortlisting, and candidate analytics.',
        category: ['Agent'],
        kind: 'boolean',
    },
    [FeatureFlags.WHATSAPP_REMINDERS_ENABLED]: {
        id: FeatureFlags.WHATSAPP_REMINDERS_ENABLED,
        title: 'WhatsApp Reminders',
        description: 'Enable sending AI interview reminders via WhatsApp.',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.PHONE_SCREENING_WHATSAPP_ENABLED]: {
        id: FeatureFlags.PHONE_SCREENING_WHATSAPP_ENABLED,
        title: 'Phone Screening on WhatsApp',
        description:
            'Message candidates on WhatsApp about AI phone screening calls (scheduled, ask for a time, missed) and let an assistant reschedule from their replies.',
        category: ['AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.EMPLOYEE_POOL]: {
        id: FeatureFlags.EMPLOYEE_POOL,
        title: 'Employee Pool',
        description: 'Show Employee Pool tabs on job details and the Talent page (coming soon).',
        category: ['Job Postings'],
        kind: 'boolean',
    },
    [FeatureFlags.AI_ANALYTICS_ENABLED]: {
        id: FeatureFlags.AI_ANALYTICS_ENABLED,
        title: 'AI Analytics Workspace',
        description: 'Enable the AI Analytics page tab and natural language query workspace.',
        category: ['Analytics'],
        kind: 'boolean',
    },
    [FeatureFlags.USE_MARKITDOWN]: {
        id: FeatureFlags.USE_MARKITDOWN,
        title: 'MarkItDown Resume Extraction',
        description: "Use MarkItDown when extracting resumes for this company's job applications.",
        category: ['Job Applications'],
        kind: 'boolean',
    },
    [FeatureFlags.JOB_ACTIVITY_TAB]: {
        id: FeatureFlags.JOB_ACTIVITY_TAB,
        title: 'Job Activity tab',
        description: 'Show the Activity tab on job details: a log of approvals, visibility, position and candidate status changes for the job.',
        category: ['Job Postings'],
        kind: 'boolean',
    },
    [FeatureFlags.JOB_APPLICATIONS_BOARD]: {
        id: FeatureFlags.JOB_APPLICATIONS_BOARD,
        title: 'Job applications board',
        description: 'Show the Table / Board switch on job applications: candidates as cards in status columns, dragged between columns to change status.',
        category: ['Job Applications'],
        kind: 'boolean',
    },
    [FeatureFlags.ADMIN_CAN_BYPASS_APPROVAL]: {
        id: FeatureFlags.ADMIN_CAN_BYPASS_APPROVAL,
        title: 'Admin can bypass approval',
        description:
            'Allow admin users in this company to immediately approve pending jobs. Intalent internal users can always bypass, even when this flag is off.',
        category: ['Job Postings'],
        kind: 'boolean',
    },
    [FeatureFlags.DARK_MODE_ENABLED]: {
        id: FeatureFlags.DARK_MODE_ENABLED,
        title: 'Dark Mode',
        description: 'Show the dark mode toggle in the user menu and allow dark theme for authenticated app routes.',
        category: ['User Interface'],
        kind: 'boolean',
    },
    [FeatureFlags.CONTRACT_BILLING]: {
        id: FeatureFlags.CONTRACT_BILLING,
        title: 'Contract billing',
        description: 'Billing for this company is handled by contract. Hide the Settings → Billing tab and block company billing portal access.',
        category: ['Access'],
        kind: 'boolean',
    },
    [FeatureFlags.RESUME_PROCESSING_CONCURRENCY]: {
        id: FeatureFlags.RESUME_PROCESSING_CONCURRENCY,
        title: 'Resume processing concurrency',
        description: 'Maximum number of resume processing jobs that can run concurrently for this company.',
        category: ['AI Screening'],
        kind: 'number',
    },
    [FeatureFlags.INTERVIEW_RECORDING_VIDEO_KBPS]: {
        id: FeatureFlags.INTERVIEW_RECORDING_VIDEO_KBPS,
        title: 'AI interview recording bitrate (kbps)',
        description:
            'Highest video bitrate for AI interview recordings (default 1000, kept between 300 and 2500). On a slower connection, as measured by the pre-interview check, recordings use a lower bitrate, frame rate and size so the live interview keeps enough upload; unmeasured connections record at up to 800.',
        category: ['AI Screening'],
        kind: 'number',
    },
    [FeatureFlags.CAREER_TALENT_POOL_APPLY]: {
        id: FeatureFlags.CAREER_TALENT_POOL_APPLY,
        title: 'Careers talent pool apply',
        description: 'Show a "Join our talent pool" general application on the career page that adds applicants to the talent pool without a job.',
        category: ['Job Applications'],
        kind: 'boolean',
    },
    [FeatureFlags.AGENT_MEMORY_ENABLED]: {
        id: FeatureFlags.AGENT_MEMORY_ENABLED,
        title: 'Agent memory',
        description:
            'Let AI agents remember candidates across interviews (phone and web screening). Off: nothing is read from or written to memory. Turning it off deletes nothing.',
        category: ['Agent', 'AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.AGENT_MEMORY_PORTABLE_ENABLED]: {
        id: FeatureFlags.AGENT_MEMORY_PORTABLE_ENABLED,
        title: 'Agent memory: share across companies',
        description:
            'Share candidate-stated facts with, and receive them from, other companies that also opted in. Has an effect only when Agent memory is on.',
        category: ['Agent', 'AI Screening'],
        kind: 'boolean',
    },
    [FeatureFlags.AGENT_MEMORY_HASHED_IDENTITY_ENABLED]: {
        id: FeatureFlags.AGENT_MEMORY_HASHED_IDENTITY_ENABLED,
        title: 'Agent memory: hashed candidate identity',
        description:
            'Store candidate email and phone in agent memory as KMS hashes instead of plain text. Affects new writes only. Has an effect only when Agent memory is on.',
        category: ['Agent', 'AI Screening'],
        kind: 'boolean',
    },
} as const satisfies Record<FeatureFlags, CompanyFeatureFlagMeta>

export function isBooleanFeatureFlag(key: FeatureFlags): boolean {
    return COMPANY_FEATURE_FLAGS[key].kind === 'boolean'
}

export function isEnumFeatureFlag(key: FeatureFlags): boolean {
    return COMPANY_FEATURE_FLAGS[key].kind === 'enum'
}

export function isNumberFeatureFlag(key: FeatureFlags): boolean {
    return COMPANY_FEATURE_FLAGS[key].kind === 'number'
}
